import type { Circuit, PlayerTrackPosition, TrackCell } from '../types/track.js';
import { TrackCellType } from '../types/track.js';
import {
  canFinishRace,
  getPlayerTrackCell,
  isOnJokerLapEntry,
  mustTakeJokerLapNow,
  needsJokerLapEntryChoice,
  normalizeMainPathIndex,
  resolveEffectiveJokerLapEntryChoice,
  resolveEnterJokerLapAtEntry,
  stepTrackPosition,
} from './jokerLap.js';
import { buildStartGridPositions, hasStartLane } from './startLane.js';
import {
  isStartFinishFinishSide,
  isStartFinishLogicalWrap,
  isStartFinishOppositeSidePair,
} from './trackSfOccupancy.js';
import { physicalGridKey, trackMovementOccupancyKey } from './trackOccupancyKey.js';
import {
  MAX_TRACK_CELL_OCCUPANCY,
  canEnterEmptyTrackCell,
  canOccupyTrackCell,
  isTrackCellOccupancyFull,
  requiresOvertakeToEnter,
} from './trackCellOccupancy.js';
import { computeTrackProgressScore } from './trackProgress.js';
import { countLapCrossingBetween } from './lapCount.js';

export { isOpeningRoundNoOvertake } from './openingRound.js';
export { trackMovementOccupancyKey } from './trackOccupancyKey.js';
export {
  MAX_TRACK_CELL_OCCUPANCY,
  canEnterEmptyTrackCell,
  canOccupyTrackCell,
  isTrackCellOccupancyFull,
  requiresOvertakeToEnter,
};

/**
 * Compte les occupants d’une clé.
 * Si `relativeTo` est fourni, ignore les rivaux côté opposé S/F (0↔pathLen-1) :
 * coexistence légale sur la ligne, pas un empilement à faire reculer.
 */
export type CountOthersOnGridFn = (
  occupancyKey: string,
  excludeIndex: number,
  relativeTo?: PlayerTrackPosition
) => number;

/** Recule le long des waypoints tant que la case finale dépasserait `maxOtherOccupants`. */
export function enforceTrackCellOccupancyLimit(
  circuit: Circuit,
  playerIndex: number,
  waypoints: PlayerTrackPosition[],
  countOthersOnGrid: CountOthersOnGridFn,
  maxOtherOccupants: number = MAX_TRACK_CELL_OCCUPANCY - 1
): { position: PlayerTrackPosition; waypoints: PlayerTrackPosition[] } {
  if (waypoints.length === 0) {
    return {
      position: { mainPathIndex: 0, jokerBranchIndex: null, startLaneIndex: null },
      waypoints: [],
    };
  }
  const wps = [...waypoints];
  let currentPos = { ...wps[wps.length - 1] };
  let guard = 0;
  const maxGuard =
    circuit.path.length +
    (circuit.jokerLap?.cells.length ?? 0) +
    (circuit.startLane?.cells.length ?? 0) +
    4;
  while (guard++ < maxGuard && wps.length > 1) {
    const key = trackMovementOccupancyKey(circuit, currentPos);
    // relativeTo = position finale : un rival 0↔pathLen-1 ne fait pas reculer le wrap.
    const others = key ? countOthersOnGrid(key, playerIndex, currentPos) : 0;
    if (!key || others <= maxOtherOccupants) break;
    wps.pop();
    currentPos = { ...wps[wps.length - 1] };
  }
  return { position: currentPos, waypoints: wps };
}

export function createInitialTrackPosition(circuit?: Circuit): PlayerTrackPosition {
  if (circuit && hasStartLane(circuit)) {
    return buildStartGridPositions(circuit, 1)[0];
  }
  return { mainPathIndex: 0, jokerBranchIndex: null, startLaneIndex: null };
}

export function trackPositionsEqual(a: PlayerTrackPosition, b: PlayerTrackPosition): boolean {
  return (
    a.mainPathIndex === b.mainPathIndex &&
    a.jokerBranchIndex === b.jokerBranchIndex &&
    a.startLaneIndex === b.startLaneIndex
  );
}

export function trackPositionGridKey(
  circuit: Circuit,
  position: PlayerTrackPosition
): string | null {
  const cell = getPlayerTrackCell(circuit, position);
  if (!cell) return null;
  return `${cell.x},${cell.y}`;
}

export type TrackMoveBonusFn = (position: PlayerTrackPosition, cell: TrackCell) => number;

export interface TrackMoveOptions {
  /** Choix à l'entrée du tour joker (multi-tours). */
  enterJokerLapChoice?: boolean | null;
  numLaps?: number;
  /** Tours déjà bouclés (pour obligation joker au dernier tour). */
  completedLaps?: number;
}

export interface TrackMoveResult {
  position: PlayerTrackPosition;
  jokerLapUsed: boolean;
  /** Cases effectivement parcourues sur le tracé. */
  stepsTaken: number;
  /** Budget initial : dé + bonus de la case de départ (avant consommation en virages). */
  moveBudget: number;
  waypoints: PlayerTrackPosition[];
  /** Arrêt à l'entrée du tour joker en attente de choix (budget restant > 0). */
  pausedAtJokerLapEntry?: boolean;
  /** Budget de déplacement restant après une pause à l'entrée. */
  remainingBudget?: number;
}

/** Budget de déplacement au lancer (dé + bonus case de départ, règle virage incluse). */
export function computeInitialMoveBudget(
  circuit: Circuit,
  from: PlayerTrackPosition,
  roll: number,
  getBonus: TrackMoveBonusFn
): number {
  const startCell = getPlayerTrackCell(circuit, from);
  if (!startCell) return 0;
  const bonus = getBonus(from, startCell);
  const safeBonus = Number.isFinite(bonus) ? bonus : 0;
  const raw =
    startCell.type === TrackCellType.TURN ? Math.ceil(roll / 2) + safeBonus : roll + safeBonus;
  // Filet softlock : un malus plateau (ex. corner −3) ne doit jamais figer le pion
  // pour tous les dés 1–6 sur un virage (D3 session #2391 Alençon).
  if (roll >= 1) return Math.max(1, raw);
  return Math.max(0, raw);
}

/**
 * Calcule le déplacement avec règles virages, en suivant le path principal et la branche joker.
 */
export function computeMoveWithTurnRulesTrack(
  circuit: Circuit,
  from: PlayerTrackPosition,
  jokerLapUsed: boolean,
  roll: number,
  getBonus: TrackMoveBonusFn,
  options?: TrackMoveOptions
): TrackMoveResult {
  const startCell = getPlayerTrackCell(circuit, from);
  if (!startCell) {
    return {
      position: from,
      jokerLapUsed,
      stepsTaken: 0,
      moveBudget: 0,
      waypoints: [from],
    };
  }

  const moveBudget = computeInitialMoveBudget(circuit, from, roll, getBonus);
  return computeMoveWithBudgetTrack(circuit, from, jokerLapUsed, moveBudget, getBonus, options);
}

/** Suite de déplacement avec un budget déjà calculé (reprise après choix tour joker). */
export function computeMoveWithBudgetTrack(
  circuit: Circuit,
  from: PlayerTrackPosition,
  jokerLapUsed: boolean,
  moveBudget: number,
  getBonus: TrackMoveBonusFn,
  options?: TrackMoveOptions
): TrackMoveResult {
  const numLaps = options?.numLaps ?? 1;
  const completedLaps = options?.completedLaps ?? 0;
  const enterJokerLapChoice = options?.enterJokerLapChoice ?? null;

  let pos = from;
  let used = jokerLapUsed;
  let stepsTaken = 0;
  const waypoints: PlayerTrackPosition[] = [{ ...from }];
  let budget = moveBudget;

  const pauseAtJokerEntry = (remainingBudget: number): TrackMoveResult => ({
    position: pos,
    jokerLapUsed: used,
    stepsTaken,
    moveBudget,
    waypoints,
    pausedAtJokerLapEntry: true,
    remainingBudget,
  });

  const checkPauseAtJokerEntry = (remainingBudget: number): TrackMoveResult | null => {
    // Budget épuisé sur l’entrée : fin de tour sur place — pas de choix (rien à
    // consommer sur la branche). Le choix viendra au lancer suivant.
    if (remainingBudget <= 0) return null;
    if (!needsJokerLapEntryChoice(circuit, pos, used, numLaps)) return null;
    const effective = resolveEffectiveJokerLapEntryChoice(
      circuit,
      numLaps,
      completedLaps,
      used,
      enterJokerLapChoice
    );
    if (effective == null) return pauseAtJokerEntry(remainingBudget);
    return null;
  };

  const applyResolvedJokerEntryStep = (): void => {
    const effective = resolveEffectiveJokerLapEntryChoice(
      circuit,
      numLaps,
      completedLaps,
      used,
      enterJokerLapChoice
    );
    if (effective == null) return;
    const enterJokerLap = resolveEnterJokerLapAtEntry(
      circuit,
      pos,
      used,
      numLaps,
      effective,
      completedLaps
    );
    const stepped = stepTrackPosition(circuit, pos, used, { enterJokerLap });
    pos = stepped.position;
    used = stepped.jokerLapUsed;
    stepsTaken++;
    waypoints.push({ ...pos });
  };

  /** Une seule réduction d’entrée en virage par déplacement (spec §7.3). */
  let cornerReductionApplied = false;

  while (budget > 0) {
    const standingPause = checkPauseAtJokerEntry(budget);
    if (standingPause) return standingPause;

    const currentCell = getPlayerTrackCell(circuit, pos);
    if (!currentCell) break;

    const effective = resolveEffectiveJokerLapEntryChoice(
      circuit,
      numLaps,
      completedLaps,
      used,
      enterJokerLapChoice
    );
    const enterJokerLap = resolveEnterJokerLapAtEntry(
      circuit,
      pos,
      used,
      numLaps,
      effective,
      completedLaps
    );
    const stepped = stepTrackPosition(circuit, pos, used, { enterJokerLap });
    const nextCell = getPlayerTrackCell(circuit, stepped.position);
    if (!nextCell) break;

    const enteringTurnFromStraight =
      currentCell.type !== TrackCellType.TURN && nextCell.type === TrackCellType.TURN;

    if (enteringTurnFromStraight && !cornerReductionApplied) {
      budget = Math.ceil(budget / 2);
      cornerReductionApplied = true;
    }
    budget--;

    pos = stepped.position;
    used = stepped.jokerLapUsed;
    stepsTaken++;
    waypoints.push({ ...pos });

    const afterStepPause = checkPauseAtJokerEntry(budget);
    if (afterStepPause) return afterStepPause;
  }

  // Pas de pause à budget 0 : rester sur l’entrée jusqu’au prochain lancer.
  if (needsJokerLapEntryChoice(circuit, pos, used, numLaps)) {
    applyResolvedJokerEntryStep();
  }

  return { position: pos, jokerLapUsed: used, stepsTaken, moveBudget, waypoints };
}

export interface TrackOvertakeAttempt {
  defenderIndex: number;
  attackerRoll: number;
  defenderRoll: number;
  attackerTotal: number;
  defenderTotal: number;
  success: boolean;
}

export interface TrackOvertakeContext {
  countOthersOnGrid: CountOthersOnGridFn;
  findDefenderOnGrid: (
    gridKey: string,
    attackerIndex: number,
    ignoreIndices?: ReadonlySet<number>
  ) => number;
  getPlayerPosition: (playerIndex: number) => PlayerTrackPosition;
  getPlayerJokerLapUsed: (playerIndex: number) => boolean;
  rollCombat: (
    attackerIndex: number,
    defenderIndex: number
  ) => {
    attackerRoll: number;
    defenderRoll: number;
    attackerTotal: number;
    defenderTotal: number;
    success: boolean;
  };
  openingRound: boolean;
  /** Choix tour joker pour le joueur en déplacement. */
  enterJokerLapChoice?: boolean | null;
  numLaps?: number;
  completedLaps?: number;
  getPlayerJokerLapEntryChoice?: (playerIndex: number) => boolean | null | undefined;
  /** Tours complétés (classement) — passage libre si pas le rival immédiat. */
  getPlayerLapCount?: (playerIndex: number) => number;
  /** Indices des pilotes encore en course (pour n-1 vs n uniquement). */
  getActivePlayerIndices?: () => readonly number[];
  /**
   * Dépassements réussis encore co-localisés : l’attaquant prioritaire
   * ne re-défie pas le colocataire au tour suivant.
   */
  sameCellOvertakePairs?: readonly { attackerIndex: number; defenderIndex: number }[];
}

export type OvertakeFreePassReason =
  'lap_lead' | 'opening_stack' | 'better_ranked' | 'not_immediate_rival';

export interface TrackFreePassAttempt {
  defenderIndex: number;
  reason: OvertakeFreePassReason;
}

/** True si l’attaquant a un ou plusieurs tours d’avance sur le défenseur (spec §5.4). */
export function hasLapLeadOverDefender(
  attackerIndex: number,
  defenderIndex: number,
  ctx: Pick<TrackOvertakeContext, 'getPlayerLapCount'>
): boolean {
  if (!ctx.getPlayerLapCount) return false;
  return ctx.getPlayerLapCount(attackerIndex) > ctx.getPlayerLapCount(defenderIndex);
}

/**
 * @deprecated Préférer `hasLapLeadOverDefender` (spec : tour d’avance uniquement).
 * Conservé pour compat tests legacy basés sur le progressScore.
 */
export function isAttackerBetterRankedThanDefender(
  circuit: Circuit,
  attackerIndex: number,
  defenderIndex: number,
  attackerPos: PlayerTrackPosition,
  ctx: Pick<TrackOvertakeContext, 'getPlayerPosition' | 'getPlayerLapCount'>
): boolean {
  return hasLapLeadOverDefender(attackerIndex, defenderIndex, ctx);
}

/**
 * True si le défenseur est exactement le concurrent immédiatement devant
 * au classement (n-1 attaque n). Sinon : doublure / lappé / saut de places
 * → pas de duel.
 *
 * `passedDefenders` : déjà dépassés dans le même move (duel réussi ou free-pass)
 * — ils ne bloquent plus le rival suivant (n après n-1).
 */
export function isImmediateRankingRivalAhead(
  circuit: Circuit,
  attackerIndex: number,
  defenderIndex: number,
  attackerPos: PlayerTrackPosition,
  ctx: Pick<
    TrackOvertakeContext,
    'getPlayerPosition' | 'getPlayerLapCount' | 'getActivePlayerIndices'
  >,
  passedDefenders?: ReadonlySet<number>
): boolean {
  if (!ctx.getPlayerLapCount || !ctx.getActivePlayerIndices) return true;
  const attackerLap = ctx.getPlayerLapCount(attackerIndex);
  const attackerScore = computeTrackProgressScore(circuit, attackerPos, attackerLap);
  const defenderLap = ctx.getPlayerLapCount(defenderIndex);
  const defenderPos = ctx.getPlayerPosition(defenderIndex);
  const defenderScore = computeTrackProgressScore(circuit, defenderPos, defenderLap);
  if (defenderScore <= attackerScore) return false;

  for (const i of ctx.getActivePlayerIndices()) {
    if (i === attackerIndex || i === defenderIndex) continue;
    if (passedDefenders?.has(i)) continue;
    const score = computeTrackProgressScore(
      circuit,
      i === attackerIndex ? attackerPos : ctx.getPlayerPosition(i),
      ctx.getPlayerLapCount(i)
    );
    if (score > attackerScore && score < defenderScore) return false;
  }
  return true;
}

/**
 * `null` = duel autorisé. Sinon passage libre (tour d’avance uniquement — spec §5.4).
 */
export function resolveOvertakeFreePassReason(
  _circuit: Circuit,
  attackerIndex: number,
  defenderIndex: number,
  _attackerPos: PlayerTrackPosition,
  ctx: Pick<
    TrackOvertakeContext,
    'getPlayerPosition' | 'getPlayerLapCount' | 'getActivePlayerIndices'
  >,
  _passedDefenders?: ReadonlySet<number>
): OvertakeFreePassReason | null {
  if (hasLapLeadOverDefender(attackerIndex, defenderIndex, ctx)) {
    return 'lap_lead';
  }
  return null;
}

/** Défenseurs déjà dépassés avec succès (priorité même case). */
function sameCellRoommateDefenders(
  pairs: readonly { attackerIndex: number; defenderIndex: number }[] | undefined,
  attackerIndex: number
): ReadonlySet<number> {
  const out = new Set<number>();
  if (!pairs?.length) return out;
  for (const p of pairs) {
    if (p.attackerIndex === attackerIndex) out.add(p.defenderIndex);
  }
  return out;
}

/**
 * Ignore les colocataires déjà dépassés (paires same-cell) pour le comptage
 * et la recherche de défenseur — pas de re-duel, pas de recul d’occupation.
 */
function withSameCellRoommateExemption(
  circuit: Circuit,
  attackerIndex: number,
  ctx: TrackOvertakeContext
): Pick<TrackOvertakeContext, 'countOthersOnGrid' | 'findDefenderOnGrid' | 'getPlayerPosition'> {
  const exempt = sameCellRoommateDefenders(ctx.sameCellOvertakePairs, attackerIndex);
  if (exempt.size === 0) {
    return {
      countOthersOnGrid: ctx.countOthersOnGrid,
      findDefenderOnGrid: ctx.findDefenderOnGrid,
      getPlayerPosition: ctx.getPlayerPosition,
    };
  }

  const countOthersOnGrid: CountOthersOnGridFn = (key, exclude, relativeTo) => {
    let n = ctx.countOthersOnGrid(key, exclude, relativeTo);
    if (n <= 0) return n;
    for (const defenderIndex of exempt) {
      if (defenderIndex === exclude) continue;
      const defenderPos = ctx.getPlayerPosition(defenderIndex);
      if (trackMovementOccupancyKey(circuit, defenderPos) !== key) continue;
      if (relativeTo && isStartFinishOppositeSidePair(circuit, relativeTo, defenderPos)) {
        continue;
      }
      n = Math.max(0, n - 1);
    }
    return n;
  };

  const findDefenderOnGrid = (
    gridKey: string,
    attacker: number,
    ignoreIndices?: ReadonlySet<number>
  ): number => {
    const merged =
      ignoreIndices && ignoreIndices.size > 0
        ? new Set<number>([...ignoreIndices, ...exempt])
        : exempt;
    return ctx.findDefenderOnGrid(gridKey, attacker, merged);
  };

  return {
    countOthersOnGrid,
    findDefenderOnGrid,
    getPlayerPosition: ctx.getPlayerPosition,
  };
}

/**
 * Nombre de pas en avant sur le tracé principal (boucle), ou `null` si l’une
 * des positions est sur une branche (joker / grille de départ).
 */
export function forwardMainPathSteps(
  circuit: Circuit,
  from: PlayerTrackPosition,
  to: PlayerTrackPosition
): number | null {
  if (
    from.startLaneIndex != null ||
    to.startLaneIndex != null ||
    from.jokerBranchIndex != null ||
    to.jokerBranchIndex != null
  ) {
    return null;
  }
  const pathLen = circuit.path.length;
  if (pathLen < 1) return null;
  const a = normalizeMainPathIndex(from.mainPathIndex, pathLen);
  const b = normalizeMainPathIndex(to.mainPathIndex, pathLen);
  return (b - a + pathLen) % pathLen;
}

/**
 * `other` est derrière `self` sur le tracé principal (chemin avant other→self
 * strictement plus court que self→other). Même case → false.
 */
export function isOtherBehindOnMainPath(
  circuit: Circuit,
  self: PlayerTrackPosition,
  other: PlayerTrackPosition
): boolean {
  const forward = forwardMainPathSteps(circuit, self, other);
  const backward = forwardMainPathSteps(circuit, other, self);
  if (forward == null || backward == null) return false;
  if (forward === 0) return false;
  return backward < forward;
}

/** Plafond du bumper post-échec : un cluster de virages, pas un tour entier. */
export const MAX_FAILED_OVERTAKE_BUMPER_STEPS = 8;

/** Occupant trop loin devant dans un cornerCluster : hors portée de duel/bumper. */
export function isOutOfOvertakeRangeOnMainPath(
  circuit: Circuit,
  attackerPos: PlayerTrackPosition,
  otherPos: PlayerTrackPosition
): boolean {
  if (isOtherBehindOnMainPath(circuit, attackerPos, otherPos)) return false;
  const dist = forwardMainPathSteps(circuit, attackerPos, otherPos);
  return dist != null && dist >= MAX_FAILED_OVERTAKE_BUMPER_STEPS;
}

/**
 * Parmi des occupants d’une même clé d’occupation, choisit le défenseur le plus
 * proche *devant* l’attaquant (pas le premier arrivé au tampon — qui peut être
 * au fond d’un cornerCluster).
 */
export function pickNearestAheadDefenderIndex(
  circuit: Circuit,
  attackerPos: PlayerTrackPosition,
  candidateIndices: readonly number[],
  getPosition: (playerIndex: number) => PlayerTrackPosition,
  getTiebreakStamp: (playerIndex: number) => number = () => 0
): number {
  let best = -1;
  let bestFwd = Infinity;
  let bestStamp = Infinity;
  for (const index of candidateIndices) {
    const pos = getPosition(index);
    if (isOtherBehindOnMainPath(circuit, attackerPos, pos)) continue;
    const fwdRaw = forwardMainPathSteps(circuit, attackerPos, pos);
    const fwd = fwdRaw ?? 0;
    const stamp = getTiebreakStamp(index);
    if (fwd < bestFwd || (fwd === bestFwd && stamp < bestStamp)) {
      bestFwd = fwd;
      bestStamp = stamp;
      best = index;
    }
  }
  return best;
}

/**
 * Case immédiatement derrière le défenseur sur le tracé (échec de dépassement).
 * Tracé principal : index précédent (boucle ; S/F → pathLen-1 derrière l’index 0).
 * Branche joker : index précédent, ou case d’entrée si en tête de branche.
 * Ligne de départ : index précédent sur la grille.
 */
export function positionImmediatelyBehindOnTrack(
  circuit: Circuit,
  defenderPos: PlayerTrackPosition
): PlayerTrackPosition | null {
  if (defenderPos.startLaneIndex != null) {
    if (defenderPos.startLaneIndex <= 0) return null;
    return {
      mainPathIndex: defenderPos.mainPathIndex,
      jokerBranchIndex: null,
      startLaneIndex: defenderPos.startLaneIndex - 1,
    };
  }
  if (defenderPos.jokerBranchIndex != null) {
    if (defenderPos.jokerBranchIndex > 0) {
      return {
        mainPathIndex: defenderPos.mainPathIndex,
        jokerBranchIndex: defenderPos.jokerBranchIndex - 1,
        startLaneIndex: null,
      };
    }
    const jl = circuit.jokerLap;
    if (!jl) return null;
    return {
      mainPathIndex: normalizeMainPathIndex(jl.entryPathIndex, circuit.path.length),
      jokerBranchIndex: null,
      startLaneIndex: null,
    };
  }
  const pathLen = circuit.path.length;
  if (pathLen < 2) return null;
  const defIdx = normalizeMainPathIndex(defenderPos.mainPathIndex, pathLen);
  // Boucle S/F (dernière case = tuile de départ) : derrière l’index 0 = pathLen-1
  // (côté arrivée), pas coreLen-1 qui saute une case.
  const behindIdx = defIdx === 0 ? pathLen - 1 : defIdx - 1;
  return {
    mainPathIndex: behindIdx,
    jokerBranchIndex: null,
    startLaneIndex: null,
  };
}

/**
 * Coupe une trajectoire client juste avant le duel : `approach` mène à la case
 * immédiatement derrière le défenseur ; `remainder` reprend depuis cette case
 * jusqu’à l’arrivée (succès ou collé derrière après échec).
 */
export function splitWaypointsAtOvertakeApproach(
  circuit: Circuit,
  waypoints: PlayerTrackPosition[],
  defenderPos: PlayerTrackPosition
): { approach: PlayerTrackPosition[]; remainder: PlayerTrackPosition[] } {
  if (waypoints.length <= 1) {
    return { approach: [...waypoints], remainder: [...waypoints] };
  }

  const behind = positionImmediatelyBehindOnTrack(circuit, defenderPos);
  let splitIdx = -1;

  for (let i = 0; i < waypoints.length; i++) {
    if (behind && trackPositionsEqual(waypoints[i], behind)) {
      splitIdx = i;
      break;
    }
    if (trackPositionsEqual(waypoints[i], defenderPos)) {
      splitIdx = Math.max(0, i - 1);
      break;
    }
  }

  if (splitIdx < 0) {
    // Pas de point d’approche identifiable : duel d’abord, puis trajet complet.
    return { approach: [waypoints[0]], remainder: [...waypoints] };
  }

  return {
    approach: waypoints.slice(0, splitIdx + 1),
    remainder: waypoints.slice(splitIdx),
  };
}

/**
 * Découpe une trajectoire en approches successives pour des duels en série.
 * Entre deux VS, le segment suivant mène jusqu’à derrière le défenseur suivant
 * (souvent la case du défenseur précédent après atterrissage).
 */
export function planSerialOvertakeApproaches(
  circuit: Circuit,
  waypoints: PlayerTrackPosition[],
  defenderPositions: readonly PlayerTrackPosition[]
): { approaches: PlayerTrackPosition[][]; remainder: PlayerTrackPosition[] } {
  let remaining = waypoints.map((wp) => ({ ...wp }));
  const approaches: PlayerTrackPosition[][] = [];
  for (const defenderPos of defenderPositions) {
    const { approach, remainder } = splitWaypointsAtOvertakeApproach(
      circuit,
      remaining,
      defenderPos
    );
    approaches.push(approach);
    remaining = remainder;
  }
  return { approaches, remainder: remaining };
}

/**
 * Avance depuis `from` jusqu’à `behind` (sans empiéter sur `defenderPos`).
 * Utilisé après un dépassement raté pour coller juste derrière le défenseur
 * (ex. cluster de virages : le duel a lieu à l’entrée du cluster).
 * Plafonné : avance au plus `MAX_FAILED_OVERTAKE_BUMPER_STEPS` (jamais un tour).
 */
function advanceTowardBehindPosition(
  circuit: Circuit,
  from: PlayerTrackPosition,
  behind: PlayerTrackPosition,
  defenderPos: PlayerTrackPosition,
  jokerLapUsedStart: boolean,
  stepOpts: { enterJokerLap: boolean }
): { position: PlayerTrackPosition; jokerLapUsed: boolean; waypoints: PlayerTrackPosition[] } {
  if (trackPositionsEqual(from, behind)) {
    return { position: from, jokerLapUsed: jokerLapUsedStart, waypoints: [] };
  }
  // Cible « derrière » via la boucle (= wrap) : ne pas bumper du tout.
  if (isOtherBehindOnMainPath(circuit, from, behind)) {
    return { position: from, jokerLapUsed: jokerLapUsedStart, waypoints: [] };
  }
  const forward = forwardMainPathSteps(circuit, from, behind);
  if (forward != null && forward <= 0) {
    return { position: from, jokerLapUsed: jokerLapUsedStart, waypoints: [] };
  }
  let pos = from;
  let used = jokerLapUsedStart;
  const waypoints: PlayerTrackPosition[] = [];
  const maxGuard = MAX_FAILED_OVERTAKE_BUMPER_STEPS;
  for (let guard = 0; guard < maxGuard; guard++) {
    if (trackPositionsEqual(pos, behind)) break;
    const stepped = stepTrackPosition(circuit, pos, used, stepOpts);
    if (trackPositionsEqual(stepped.position, defenderPos)) break;
    if (trackPositionsEqual(stepped.position, pos)) break;
    pos = stepped.position;
    used = stepped.jokerLapUsed;
    waypoints.push({ ...pos });
    if (trackPositionsEqual(pos, behind)) break;
  }
  return { position: pos, jokerLapUsed: used, waypoints };
}

/**
 * Occupants sur la même case physique (x,y).
 * Ouverture + cornerCluster : la capacité 2 se juge sur la tuile, pas sur tout
 * le bucket (sinon solo + duo plus loin saturent le cluster — log Sylvie).
 */
function countOthersOnPhysicalCell(
  circuit: Circuit,
  playerIndex: number,
  position: PlayerTrackPosition,
  ctx: Pick<TrackOvertakeContext, 'getPlayerPosition' | 'getActivePlayerIndices'>
): number {
  const target = physicalGridKey(circuit, position);
  if (!target) return 0;
  const indices = ctx.getActivePlayerIndices?.() ?? [];
  let n = 0;
  for (const i of indices) {
    if (i === playerIndex) continue;
    const other = ctx.getPlayerPosition(i);
    if (physicalGridKey(circuit, other) !== target) continue;
    if (isStartFinishOppositeSidePair(circuit, position, other)) continue;
    n += 1;
  }
  return n;
}

/**
 * Atterrissage ouverture : clé normale → countOthersOnGrid ; cornerCluster →
 * comptage physique si le roster est dispo, sinon repli sur la clé cluster.
 */
function countOthersForOpeningDestination(
  circuit: Circuit,
  playerIndex: number,
  position: PlayerTrackPosition,
  ctx: TrackOvertakeContext
): number {
  const key = trackMovementOccupancyKey(circuit, position);
  if (!key) return 0;
  if (key.startsWith('cornerCluster:') && ctx.getActivePlayerIndices) {
    return countOthersOnPhysicalCell(circuit, playerIndex, position, ctx);
  }
  return ctx.countOthersOnGrid(key, playerIndex, position);
}

function openingDestinationCountOthers(
  circuit: Circuit,
  ctx: TrackOvertakeContext
): CountOthersOnGridFn {
  return (_key, exclude, relativeTo) => {
    if (!relativeTo) return 0;
    return countOthersForOpeningDestination(circuit, exclude, relativeTo, ctx);
  };
}

/**
 * Co-occupants de la case d’atterrissage (ouverture) — pour enregistrer un
 * freePass `opening_stack` (paire same-cell sans priorityDefender).
 */
function listOpeningCoOccupants(
  circuit: Circuit,
  playerIndex: number,
  position: PlayerTrackPosition,
  ctx: TrackOvertakeContext
): number[] {
  const indices = ctx.getActivePlayerIndices?.() ?? [];
  if (indices.length === 0) return [];
  const key = trackMovementOccupancyKey(circuit, position);
  if (!key) return [];
  const physicalTarget = key.startsWith('cornerCluster:')
    ? physicalGridKey(circuit, position)
    : null;
  const out: number[] = [];
  for (const i of indices) {
    if (i === playerIndex) continue;
    const other = ctx.getPlayerPosition(i);
    if (isStartFinishOppositeSidePair(circuit, position, other)) continue;
    if (physicalTarget) {
      if (physicalGridKey(circuit, other) !== physicalTarget) continue;
    } else if (trackMovementOccupancyKey(circuit, other) !== key) {
      continue;
    }
    out.push(i);
  }
  return out;
}

/**
 * Occupants qui bloquent le pas vers `next`.
 * Wrap S/F pathLen-1→0 : un rival encore à pathLen-1 ne bloque pas (même grille, côté arrivée).
 * Entrée sur pathLen-1 face à un rival déjà en 0 : côtés opposés, pas de blocage
 * (le duel n’a lieu qu’au wrap vers la 1ère case).
 */
function countOthersBlockingStep(
  circuit: Circuit,
  from: PlayerTrackPosition,
  next: PlayerTrackPosition,
  nextKey: string,
  playerIndex: number,
  ctx: Pick<TrackOvertakeContext, 'countOthersOnGrid' | 'findDefenderOnGrid' | 'getPlayerPosition'>
): number {
  if (isStartFinishLogicalWrap(circuit, from, next)) {
    const defenderIndex = ctx.findDefenderOnGrid(nextKey, playerIndex);
    if (defenderIndex >= 0) {
      const defenderPos = ctx.getPlayerPosition(defenderIndex);
      // Sur la case S/F (max 2), un rival encore à l’arrivée n’empêche pas le wrap.
      if (isStartFinishFinishSide(circuit, defenderPos)) return 0;
    }
    // Compte réel (1 = partage possible, 2 = plein) — pas un booléen 0/1.
    return ctx.countOthersOnGrid(nextKey, playerIndex, next);
  }
  // relativeTo = case d’arrivée : ignore un rival côté opposé S/F (0 ↔ pathLen-1).
  const raw = ctx.countOthersOnGrid(nextKey, playerIndex, next);
  // Corner cluster long : des rivaux à > bumper pas devant ne doivent pas
  // saturer la capacité 2 (sinon softlock à l’entrée — logs Laurent/Vincent).
  if (!nextKey.startsWith('cornerCluster:') || raw <= 0) return raw;
  let nearby = 0;
  const seen = new Set<number>();
  for (let guard = 0; guard < raw + 4; guard++) {
    const defenderIndex = ctx.findDefenderOnGrid(nextKey, playerIndex, seen);
    if (defenderIndex < 0) break;
    // Garde si le callback ignore `ignoreIndices` (sinon boucle / double-compte).
    if (seen.has(defenderIndex)) break;
    seen.add(defenderIndex);
    const defenderPos = ctx.getPlayerPosition(defenderIndex);
    if (isStartFinishOppositeSidePair(circuit, next, defenderPos)) continue;
    if (isOutOfOvertakeRangeOnMainPath(circuit, from, defenderPos)) continue;
    nearby += 1;
  }
  return nearby;
}

function findDefenderBlockingStep(
  circuit: Circuit,
  from: PlayerTrackPosition,
  next: PlayerTrackPosition,
  nextKey: string,
  playerIndex: number,
  ctx: Pick<TrackOvertakeContext, 'findDefenderOnGrid' | 'getPlayerPosition'>,
  ignoreIndices?: ReadonlySet<number>
): number {
  if (isStartFinishLogicalWrap(circuit, from, next)) {
    const defenderIndex = ctx.findDefenderOnGrid(nextKey, playerIndex, ignoreIndices);
    if (defenderIndex < 0) return -1;
    if (isStartFinishFinishSide(circuit, ctx.getPlayerPosition(defenderIndex))) return -1;
    return defenderIndex;
  }
  const defenderIndex = ctx.findDefenderOnGrid(nextKey, playerIndex, ignoreIndices);
  if (defenderIndex < 0) return -1;
  if (isStartFinishOppositeSidePair(circuit, next, ctx.getPlayerPosition(defenderIndex))) {
    return -1;
  }
  return defenderIndex;
}

function stepOptionsForPlayer(
  circuit: Circuit,
  position: PlayerTrackPosition,
  jokerLapUsed: boolean,
  movingPlayerIndex: number,
  attackerPlayerIndex: number,
  ctx: TrackOvertakeContext
): { enterJokerLap: boolean } {
  const numLaps = ctx.numLaps ?? 1;
  const completedLaps = ctx.completedLaps ?? 0;
  const explicit =
    movingPlayerIndex === attackerPlayerIndex
      ? ctx.enterJokerLapChoice
      : ctx.getPlayerJokerLapEntryChoice?.(movingPlayerIndex);
  const effective = resolveEffectiveJokerLapEntryChoice(
    circuit,
    numLaps,
    completedLaps,
    jokerLapUsed,
    explicit ?? null
  );
  const enterJokerLap = resolveEnterJokerLapAtEntry(
    circuit,
    position,
    jokerLapUsed,
    numLaps,
    effective,
    completedLaps
  );
  return { enterJokerLap };
}

export interface TrackMoveResolution {
  position: PlayerTrackPosition;
  jokerLapUsed: boolean;
  priorityDefenderIndex: number | null;
  overtakes: TrackOvertakeAttempt[];
  /** Passages sans duel (mieux classé / pas le n devant). */
  freePasses: TrackFreePassAttempt[];
  waypoints: PlayerTrackPosition[];
  /** Arrêt à l’entrée joker en attente de choix (budget non consommé au-delà). */
  pausedAtJokerLapEntry?: boolean;
}

/**
 * Résout un déplacement case par case avec dépassements (grille physique, pas index path seul).
 */
export function resolveTrackMoveWithOvertakes(
  circuit: Circuit,
  playerIndex: number,
  from: PlayerTrackPosition,
  jokerLapUsedStart: boolean,
  stepsTaken: number,
  ctx: TrackOvertakeContext
): TrackMoveResolution {
  let currentPos = from;
  let currentUsed = jokerLapUsedStart;
  let remainingSteps = stepsTaken;
  let priorityDefenderIndex: number | null = null;
  let failedOvertakeBumper = false;
  /** Colocataire derrière dans un cluster : on avance sans duel, donc le
   * finishMove doit autoriser 1 autre sur la clé d’occupation (sinon le
   * enforce rewind jusqu’au départ — cas Sylvie tour 6). */
  let allowSharedOccupancyWithBehindRoommate = false;
  /** Entrée dans un cluster malgré des occupants hors portée bumper : ne pas
   * laisser enforceOccupancy rembobiner (capacité 2 saturée par des lointains). */
  let ignoreFarClusterOccupancyCap = false;
  const overtakes: TrackOvertakeAttempt[] = [];
  const freePasses: TrackFreePassAttempt[] = [];
  const waypoints: PlayerTrackPosition[] = [{ ...from }];
  const openingRound = ctx.openingRound;
  const occupancyCtx = withSameCellRoommateExemption(circuit, playerIndex, ctx);
  const moveCtx: TrackOvertakeContext = { ...ctx, ...occupancyCtx };

  const challengedDefenders = new Set<number>();
  /** Défenseurs déjà passés ce move (duel OK ou free-pass) — libèrent le n suivant. */
  const passedDefendersThisMove = new Set<number>();
  let pausedAtJokerLapEntry = false;

  const occupantsOnGrid = (gridKey: string): number =>
    occupancyCtx.countOthersOnGrid(gridKey, playerIndex);

  const shouldPauseForJokerChoice = (
    pos: PlayerTrackPosition,
    used: boolean,
    budgetLeft: number
  ): boolean => {
    if (budgetLeft <= 0) return false;
    const numLaps = moveCtx.numLaps ?? 1;
    const completedLaps = moveCtx.completedLaps ?? 0;
    if (!needsJokerLapEntryChoice(circuit, pos, used, numLaps)) return false;
    if (mustTakeJokerLapNow(circuit, numLaps, completedLaps, used)) return false;
    const effective = resolveEffectiveJokerLapEntryChoice(
      circuit,
      numLaps,
      completedLaps,
      used,
      moveCtx.enterJokerLapChoice ?? null
    );
    return effective == null;
  };

  const finishMove = (
    position: PlayerTrackPosition,
    jokerLapUsed: boolean,
    priority: number | null,
    overtakesOut: TrackOvertakeAttempt[],
    waypointsOut: PlayerTrackPosition[],
    maxOtherOccupants: number = MAX_TRACK_CELL_OCCUPANCY - 1
  ): TrackMoveResolution => {
    // Dépassement réussi / bumper : un seul waypoint peut sauter des cases
    // (ex. path 10→12). Densifier pour que QA + animation visitent chaque case.
    const densified = densifyTrackWaypoints(circuit, waypointsOut, jokerLapUsed);
    const capped = enforceTrackCellOccupancyLimit(
      circuit,
      playerIndex,
      densified,
      occupancyCtx.countOthersOnGrid,
      maxOtherOccupants
    );
    // Si enforce a reculé hors de l’entrée, ne pas signaler une pause fantôme.
    // remainingSteps = budget encore consommable après la pause.
    const pauseHere =
      pausedAtJokerLapEntry &&
      shouldPauseForJokerChoice(capped.position, jokerLapUsed, remainingSteps);
    return {
      position: capped.position,
      jokerLapUsed,
      priorityDefenderIndex: priority,
      overtakes: overtakesOut,
      freePasses: [...freePasses],
      waypoints: capped.waypoints,
      pausedAtJokerLapEntry: pauseHere || undefined,
    };
  };

  if (shouldPauseForJokerChoice(currentPos, currentUsed, remainingSteps)) {
    return {
      position: currentPos,
      jokerLapUsed: currentUsed,
      priorityDefenderIndex: null,
      overtakes: [],
      freePasses: [],
      waypoints: [{ ...currentPos }],
      pausedAtJokerLapEntry: true,
    };
  }

  if (openingRound) {
    // Départ (spec §6) : aucun duel. On avance le budget entier, on traverse les
    // cases pleines, puis on recule la destination tant qu’elle est pleine
    // (interdiction de terminer sur une case à 2 occupants).
    // Le choix joker reste obligatoire dès l’arrivée sur l’entrée.
    // Partage à 1 occupant : PAS de priorityDefender — le premier arrivé
    // (tampon d’arrivée) rejoue en premier ; le second ne vole pas la priorité.
    let pos = currentPos;
    let used = currentUsed;
    const planned: PlayerTrackPosition[] = [{ ...pos }];
    for (let i = 0; i < remainingSteps; i++) {
      const stepOpts = stepOptionsForPlayer(circuit, pos, used, playerIndex, playerIndex, moveCtx);
      const stepped = stepTrackPosition(circuit, pos, used, stepOpts);
      const nextKey = trackMovementOccupancyKey(circuit, stepped.position);
      if (!nextKey) break;
      pos = stepped.position;
      used = stepped.jokerLapUsed;
      planned.push({ ...pos });
      const budgetLeft = remainingSteps - (planned.length - 1);
      if (shouldPauseForJokerChoice(pos, used, budgetLeft)) {
        pausedAtJokerLapEntry = true;
        break;
      }
    }
    // Ne jamais terminer sur une case pleine : trim de la fin.
    // cornerCluster : capacité sur la tuile physique (countOthersForOpeningDestination).
    // Exception : pause joker — on doit rester sur l’entrée même si elle est pleine.
    const openingLandCount = openingDestinationCountOthers(circuit, moveCtx);
    if (!pausedAtJokerLapEntry) {
      while (planned.length > 1) {
        const finalPos = planned[planned.length - 1];
        const others = countOthersForOpeningDestination(circuit, playerIndex, finalPos, moveCtx);
        if (!isTrackCellOccupancyFull(others)) break;
        planned.pop();
      }
    }
    const densified = densifyTrackWaypoints(circuit, planned, used);
    if (pausedAtJokerLapEntry) {
      return finishMove(
        densified[densified.length - 1] ?? pos,
        used,
        null,
        overtakes,
        densified,
        MAX_TRACK_CELL_OCCUPANCY
      );
    }
    // Filet sans repasser par finishMove (sinon cap cluster re-bloque le solo).
    const openingCapped = enforceTrackCellOccupancyLimit(
      circuit,
      playerIndex,
      densified,
      openingLandCount,
      MAX_TRACK_CELL_OCCUPANCY - 1
    );
    // Partage légal à 1 sans duel : enregistre la paire pour l’invariant post-ouverture
    // et l’exemption colocataire — sans priorityDefender (1er arrivé garde le tampon).
    for (const defenderIndex of listOpeningCoOccupants(
      circuit,
      playerIndex,
      openingCapped.position,
      moveCtx
    )) {
      freePasses.push({ defenderIndex, reason: 'opening_stack' });
    }
    return {
      position: openingCapped.position,
      jokerLapUsed: used,
      priorityDefenderIndex: null,
      overtakes,
      freePasses: [...freePasses],
      waypoints: openingCapped.waypoints,
    };
  }

  while (remainingSteps > 0) {
    const stepOpts = stepOptionsForPlayer(
      circuit,
      currentPos,
      currentUsed,
      playerIndex,
      playerIndex,
      moveCtx
    );
    const stepped = stepTrackPosition(circuit, currentPos, currentUsed, stepOpts);
    const nextKey = trackMovementOccupancyKey(circuit, stepped.position);
    if (!nextKey) break;

    const othersOnNext = countOthersBlockingStep(
      circuit,
      currentPos,
      stepped.position,
      nextKey,
      playerIndex,
      occupancyCtx
    );
    if (nextKey.startsWith('cornerCluster:')) {
      const rawOthers = occupancyCtx.countOthersOnGrid(nextKey, playerIndex, stepped.position);
      if (rawOthers > othersOnNext) ignoreFarClusterOccupancyCap = true;
    }
    if (isTrackCellOccupancyFull(othersOnNext)) break;

    if (canEnterEmptyTrackCell(othersOnNext)) {
      currentPos = stepped.position;
      currentUsed = stepped.jokerLapUsed;
      remainingSteps--;
      priorityDefenderIndex = null;
      waypoints.push({ ...currentPos });
      if (shouldPauseForJokerChoice(currentPos, currentUsed, remainingSteps)) {
        pausedAtJokerLapEntry = true;
        break;
      }
      continue;
    }

    const defenderIgnore = new Set<number>(challengedDefenders);
    let defenderIndex = findDefenderBlockingStep(
      circuit,
      currentPos,
      stepped.position,
      nextKey,
      playerIndex,
      occupancyCtx,
      defenderIgnore
    );
    // Cluster de virages uniquement : un colocataire déjà derrière sur le path
    // n’est pas un défenseur — avancer sans duel (évite le bumper « tour complet »).
    // Idem pour un occupant trop loin devant (hors portée bumper) : pas de duel fantôme.
    // Sur une case physique seule (spirale, re-visite), on ne contourne pas.
    const nextIsCornerCluster = nextKey.startsWith('cornerCluster:');
    let skippedNonBlockingInCluster = false;
    while (defenderIndex >= 0 && nextIsCornerCluster) {
      const candidatePos = moveCtx.getPlayerPosition(defenderIndex);
      const skipBehind = isOtherBehindOnMainPath(circuit, currentPos, candidatePos);
      const skipFar = isOutOfOvertakeRangeOnMainPath(circuit, currentPos, candidatePos);
      if (!skipBehind && !skipFar) break;
      skippedNonBlockingInCluster = true;
      if (skipFar) ignoreFarClusterOccupancyCap = true;
      if (skipBehind) allowSharedOccupancyWithBehindRoommate = true;
      defenderIgnore.add(defenderIndex);
      const nextDefender = findDefenderBlockingStep(
        circuit,
        currentPos,
        stepped.position,
        nextKey,
        playerIndex,
        occupancyCtx,
        defenderIgnore
      );
      // Garde : si le callback ignore `ignoreIndices`, ne pas boucler.
      if (nextDefender === defenderIndex || defenderIgnore.has(nextDefender)) {
        defenderIndex = -1;
        break;
      }
      defenderIndex = nextDefender;
    }
    if (defenderIndex < 0) {
      if (skippedNonBlockingInCluster) {
        if (!ignoreFarClusterOccupancyCap) allowSharedOccupancyWithBehindRoommate = true;
        currentPos = stepped.position;
        currentUsed = stepped.jokerLapUsed;
        remainingSteps--;
        priorityDefenderIndex = null;
        waypoints.push({ ...currentPos });
        continue;
      }
      break;
    }

    if (challengedDefenders.has(defenderIndex)) {
      // Déjà défié : pas de second duel, et pas de partage sans nouveau dépassement réussi.
      break;
    }

    // Duel seulement si défenseur = concurrent immédiat devant au départ
    // du move (n-1 → n). Classement figé sur `from` : avancer sur la piste
    // ne doit pas « sauter » n-1 pour dueller n. Les défenseurs déjà passés
    // dans ce move ne comptent plus entre n-1 et n. Sinon : laisse passer.
    const freePassReason = resolveOvertakeFreePassReason(
      circuit,
      playerIndex,
      defenderIndex,
      from,
      moveCtx,
      passedDefendersThisMove
    );
    if (freePassReason != null) {
      challengedDefenders.add(defenderIndex);
      passedDefendersThisMove.add(defenderIndex);
      freePasses.push({ defenderIndex, reason: freePassReason });
      // Autorise la case partagée pour enforceOccupancy (comme un duel réussi).
      allowSharedOccupancyWithBehindRoommate = true;
      priorityDefenderIndex = defenderIndex;
      currentPos = stepped.position;
      currentUsed = stepped.jokerLapUsed;
      remainingSteps--;
      waypoints.push({ ...currentPos });
      if (shouldPauseForJokerChoice(currentPos, currentUsed, remainingSteps)) {
        pausedAtJokerLapEntry = true;
        break;
      }
      continue;
    }

    challengedDefenders.add(defenderIndex);

    const defenderPos = moveCtx.getPlayerPosition(defenderIndex);
    const combat = moveCtx.rollCombat(playerIndex, defenderIndex);
    overtakes.push({
      defenderIndex,
      attackerRoll: combat.attackerRoll,
      defenderRoll: combat.defenderRoll,
      attackerTotal: combat.attackerTotal,
      defenderTotal: combat.defenderTotal,
      success: combat.success,
    });

    remainingSteps--;
    if (!combat.success) {
      priorityDefenderIndex = null;
      const behind = positionImmediatelyBehindOnTrack(circuit, defenderPos);
      if (behind) {
        const advanced = advanceTowardBehindPosition(
          circuit,
          currentPos,
          behind,
          defenderPos,
          currentUsed,
          stepOpts
        );
        if (advanced.waypoints.length > 0) {
          currentPos = advanced.position;
          currentUsed = advanced.jokerLapUsed;
          waypoints.push(...advanced.waypoints);
          failedOvertakeBumper = true;
        }
        // Pas de téléport vers `behind` : bumper plafonné (avance partielle OK).
      }
      break;
    }

    // Défenseur trop loin dans un cluster : ne pas téléporter / ne pas garder un success fantôme.
    const distToDefender = forwardMainPathSteps(circuit, currentPos, defenderPos);
    if (distToDefender != null && distToDefender >= MAX_FAILED_OVERTAKE_BUMPER_STEPS) {
      overtakes.pop();
      remainingSteps++;
      priorityDefenderIndex = null;
      break;
    }

    passedDefendersThisMove.add(defenderIndex);

    // Atterrissage sur la case du défenseur (coût = le step déjà décrémenté).
    // Pas de saut gratuit devant : les cases restantes reprennent via la boucle.
    const defenderKey = trackMovementOccupancyKey(circuit, defenderPos);
    if (
      defenderKey &&
      !canOccupyTrackCell(occupantsOnGrid(defenderKey), {
        openingRound: false,
        overtakeSucceeded: true,
      })
    ) {
      priorityDefenderIndex = null;
      break;
    }
    currentPos = { ...defenderPos };
    // Priorité même case tant qu’on n’a pas quitté le défenseur.
    priorityDefenderIndex = defenderIndex;
    waypoints.push({ ...currentPos });
    allowSharedOccupancyWithBehindRoommate = true;

    if (shouldPauseForJokerChoice(currentPos, currentUsed, remainingSteps)) {
      pausedAtJokerLapEntry = true;
      break;
    }

    if (remainingSteps <= 0) break;
    // Budget restant : la boucle tente la case suivante ; priority cleared à la sortie réelle.
  }

  // Si on termine encore colocalisé avec le dernier défenseur dépassé, garder la priorité
  // même si un step suivant a échoué (case pleine / pas de défenseur identifiable).
  let finalPriority = priorityDefenderIndex;
  if (finalPriority == null && overtakes.some((o) => o.success)) {
    const lastSuccess = [...overtakes].reverse().find((o) => o.success);
    if (lastSuccess) {
      const defPos = moveCtx.getPlayerPosition(lastSuccess.defenderIndex);
      const curKey = trackMovementOccupancyKey(circuit, currentPos);
      const defKey = trackMovementOccupancyKey(circuit, defPos);
      if (curKey != null && curKey === defKey) {
        finalPriority = lastSuccess.defenderIndex;
      }
    }
  }
  return finishMove(
    currentPos,
    currentUsed,
    finalPriority,
    overtakes,
    waypoints,
    ignoreFarClusterOccupancyCap
      ? 99
      : finalPriority != null || failedOvertakeBumper || allowSharedOccupancyWithBehindRoommate
        ? MAX_TRACK_CELL_OCCUPANCY - 1
        : 0
  );
}

/** Direction du prochain segment pour le placement des pions. */
export function getTrackForwardDelta(
  circuit: Circuit,
  position: PlayerTrackPosition,
  options?: { enterJokerLap?: boolean }
): { dx: number; dy: number } {
  const curr = getPlayerTrackCell(circuit, position);
  if (!curr) return { dx: 1, dy: 0 };
  const next = stepTrackPosition(circuit, position, false, {
    enterJokerLap: options?.enterJokerLap ?? false,
  });
  const nextCell = getPlayerTrackCell(circuit, next.position);
  if (!nextCell) return { dx: 1, dy: 0 };
  return { dx: nextCell.x - curr.x, dy: nextCell.y - curr.y };
}

/**
 * Waypoints case par case jusqu'à la cible, pour l'animation client.
 * `maxSteps` ne tronque plus le trajet : un plafond trop court collait la
 * cible en un seul segment (la voiture se téléportait sur la case d'arrivée).
 */
export function buildTrackWaypointsToTarget(
  circuit: Circuit,
  from: PlayerTrackPosition,
  target: PlayerTrackPosition,
  maxSteps: number,
  jokerLapUsed: boolean,
  options?: { targetJokerLapUsed?: boolean }
): PlayerTrackPosition[] {
  const waypoints: PlayerTrackPosition[] = [{ ...from }];
  if (trackPositionsEqual(from, target)) return waypoints;

  const targetUsed = options?.targetJokerLapUsed ?? jokerLapUsed;
  let pos = from;
  let used = jokerLapUsed;
  const guardLimit = Math.max(
    maxSteps,
    circuit.path.length +
      (circuit.jokerLap?.cells.length ?? 0) +
      (circuit.startLane?.cells.length ?? 0) +
      4
  );
  for (let i = 0; i < guardLimit; i++) {
    if (trackPositionsEqual(pos, target)) break;
    const enterJokerLap =
      !used &&
      isOnJokerLapEntry(circuit, pos) &&
      (target.jokerBranchIndex != null || (!jokerLapUsed && targetUsed));
    const stepped = stepTrackPosition(circuit, pos, used, { enterJokerLap });
    if (trackPositionsEqual(stepped.position, pos)) break;
    pos = stepped.position;
    used = stepped.jokerLapUsed;
    waypoints.push({ ...pos });
    if (trackPositionsEqual(pos, target)) break;
  }
  const last = waypoints[waypoints.length - 1];
  if (last && !trackPositionsEqual(last, target) && areTrackCellsAdjacent(circuit, last, target)) {
    waypoints.push({ ...target });
  }
  return waypoints;
}

function areTrackCellsAdjacent(
  circuit: Circuit,
  a: PlayerTrackPosition,
  b: PlayerTrackPosition
): boolean {
  const ca = getPlayerTrackCell(circuit, a);
  const cb = getPlayerTrackCell(circuit, b);
  if (!ca || !cb) return false;
  return Math.abs(ca.x - cb.x) + Math.abs(ca.y - cb.y) <= 1;
}

/** True si le trajet déjà parcouru a consommé la dernière case joker. */
function jokerLapAlreadyConsumedInChain(
  circuit: Circuit,
  chain: readonly PlayerTrackPosition[]
): boolean {
  const jl = circuit.jokerLap;
  if (!jl?.cells.length) return false;
  const lastBranch = jl.cells.length - 1;
  return chain.some((wp) => wp.jokerBranchIndex === lastBranch);
}

/** État `jokerLapUsed` au waypoint `from` pour densifier sans empoisonner l’entrée. */
function jokerUsedAtDensifyFrom(
  circuit: Circuit,
  from: PlayerTrackPosition,
  chainSoFar: readonly PlayerTrackPosition[]
): boolean {
  const jl = circuit.jokerLap;
  if (!jl?.cells.length) return false;
  if (from.jokerBranchIndex != null) {
    return from.jokerBranchIndex >= jl.cells.length - 1;
  }
  return jokerLapAlreadyConsumedInChain(circuit, chainSoFar);
}

/**
 * Comble les trous d'une trajectoire déjà calculée (dépassement qui pose
 * l'arrivée deux cases plus loin) pour que l'animation visite chaque case.
 *
 * Important : ne pas passer le flag joker « fin de move » à tous les ponts —
 * sinon l’entrée (used=true) refuse la branche et densify boucle sur le principal
 * (tour de circuit fantôme avant le joker).
 */
export function densifyTrackWaypoints(
  circuit: Circuit,
  waypoints: readonly PlayerTrackPosition[],
  _endJokerLapUsed = false
): PlayerTrackPosition[] {
  if (waypoints.length <= 1) return waypoints.map((wp) => ({ ...wp }));
  const out: PlayerTrackPosition[] = [{ ...waypoints[0] }];
  const budget =
    circuit.path.length +
    (circuit.jokerLap?.cells.length ?? 0) +
    (circuit.startLane?.cells.length ?? 0) +
    4;
  for (let i = 1; i < waypoints.length; i++) {
    const from = out[out.length - 1];
    const target = waypoints[i];
    if (trackPositionsEqual(from, target)) continue;
    const usedAtFrom = jokerUsedAtDensifyFrom(circuit, from, out);
    const bridge = buildTrackWaypointsToTarget(circuit, from, target, budget, usedAtFrom, {
      targetJokerLapUsed: target.jokerBranchIndex != null || from.jokerBranchIndex != null,
    });
    for (const step of bridge.slice(1)) out.push(step);
    const last = out[out.length - 1];
    if (
      last &&
      !trackPositionsEqual(last, target) &&
      areTrackCellsAdjacent(circuit, last, target)
    ) {
      out.push({ ...target });
    }
  }
  return out;
}

export type TrackMoveEvent =
  | {
      type: 'initial_roll';
      roll: number;
      bonus: number;
      section: string;
      moveBudget: number;
    }
  | {
      type: 'cell';
      from: PlayerTrackPosition;
      to: PlayerTrackPosition;
      remainingBefore: number;
      remainingAfter: number;
      traversedFull?: boolean;
    }
  | { type: 'corner_reduction'; before: number; after: number }
  | {
      type: 'overtake';
      defenderIndex: number;
      attackerRoll: number;
      defenderRoll: number;
      attackerTotal: number;
      defenderTotal: number;
      success: boolean;
    }
  | { type: 'free_pass'; defenderIndex: number; reason: OvertakeFreePassReason }
  | { type: 'block'; reason: string; remainingLost: number }
  | { type: 'lap_crossing'; before: number; after: number }
  | { type: 'finish_pending' }
  | { type: 'stop'; reason: string };

export interface ExecuteTrackMoveInput {
  circuit: Circuit;
  playerIndex: number;
  from: PlayerTrackPosition;
  jokerLapUsed: boolean;
  roll: number;
  getBonus: TrackMoveBonusFn;
  ctx: TrackOvertakeContext;
  options?: TrackMoveOptions;
  /** Budget déjà calculé (reprise après pause joker). */
  resumeBudget?: number;
}

export interface ExecuteTrackMoveResult {
  position: PlayerTrackPosition;
  jokerLapUsed: boolean;
  moveBudget: number;
  stepsTaken: number;
  remainingBudget: number;
  waypoints: PlayerTrackPosition[];
  priorityDefenderIndex: number | null;
  overtakes: TrackOvertakeAttempt[];
  freePasses: TrackFreePassAttempt[];
  events: TrackMoveEvent[];
  pausedAtJokerLapEntry?: boolean;
  finishPending: boolean;
  lapsGained: number;
  stopReason: string;
}

function cellSectionLabel(cell: TrackCell | null | undefined): string {
  if (!cell) return 'unknown';
  switch (cell.type) {
    case TrackCellType.TURN:
      return 'virage';
    case TrackCellType.START:
    case TrackCellType.START_LANE:
      return 'depart';
    case TrackCellType.JOKER_LAP:
      return 'joker';
    default:
      return 'ligne_droite';
  }
}

/**
 * Moteur de déplacement unifié (spec Rallycross) : une seule boucle case-par-case
 * (budget, virage 1×, joker, occupation, duels, départ, jonction, logs).
 */
export function executeTrackMove(input: ExecuteTrackMoveInput): ExecuteTrackMoveResult {
  const {
    circuit,
    playerIndex,
    from,
    jokerLapUsed: jokerLapUsedStart,
    roll,
    getBonus,
    ctx,
    options,
    resumeBudget,
  } = input;

  const numLaps = options?.numLaps ?? ctx.numLaps ?? 1;
  const completedLapsStart = options?.completedLaps ?? ctx.completedLaps ?? 0;
  const enterJokerLapChoice =
    options?.enterJokerLapChoice !== undefined
      ? options.enterJokerLapChoice
      : (ctx.enterJokerLapChoice ?? null);

  const moveCtx: TrackOvertakeContext = {
    ...ctx,
    numLaps,
    completedLaps: completedLapsStart,
    enterJokerLapChoice,
  };

  const startCell = getPlayerTrackCell(circuit, from);
  const bonus = startCell ? getBonus(from, startCell) : 0;
  const safeBonus = Number.isFinite(bonus) ? bonus : 0;
  const moveBudget =
    resumeBudget != null ? resumeBudget : computeInitialMoveBudget(circuit, from, roll, getBonus);

  const events: TrackMoveEvent[] = [];
  if (resumeBudget == null) {
    events.push({
      type: 'initial_roll',
      roll,
      bonus: safeBonus,
      section: cellSectionLabel(startCell),
      moveBudget,
    });
  }

  const emptyResult = (reason: string): ExecuteTrackMoveResult => ({
    position: from,
    jokerLapUsed: jokerLapUsedStart,
    moveBudget,
    stepsTaken: 0,
    remainingBudget: 0,
    waypoints: [{ ...from }],
    priorityDefenderIndex: null,
    overtakes: [],
    freePasses: [],
    events: [...events, { type: 'stop', reason }],
    finishPending: false,
    lapsGained: 0,
    stopReason: reason,
  });

  if (moveBudget <= 0) {
    return emptyResult('budget_zero');
  }

  const optimistic = computeMoveWithBudgetTrack(
    circuit,
    from,
    jokerLapUsedStart,
    moveBudget,
    getBonus,
    {
      numLaps,
      completedLaps: completedLapsStart,
      enterJokerLapChoice,
    }
  );

  const resolved = resolveTrackMoveWithOvertakes(
    circuit,
    playerIndex,
    from,
    jokerLapUsedStart,
    optimistic.stepsTaken,
    moveCtx
  );

  const { lapsGained } = appendCornerAndCellEventsFromWaypoints(
    circuit,
    resolved.waypoints,
    moveBudget,
    events,
    moveCtx,
    playerIndex,
    completedLapsStart
  );

  for (const o of resolved.overtakes) {
    events.push({
      type: 'overtake',
      defenderIndex: o.defenderIndex,
      attackerRoll: o.attackerRoll,
      defenderRoll: o.defenderRoll,
      attackerTotal: o.attackerTotal,
      defenderTotal: o.defenderTotal,
      success: o.success,
    });
  }
  for (const fp of resolved.freePasses) {
    events.push({ type: 'free_pass', defenderIndex: fp.defenderIndex, reason: fp.reason });
  }

  let stopReason = 'budget_exhausted';
  if (resolved.overtakes.some((o) => !o.success)) stopReason = 'duel_lost';
  const stepsTaken = Math.max(0, resolved.waypoints.length - 1);
  if (stepsTaken < optimistic.stepsTaken && stopReason === 'budget_exhausted') {
    stopReason = 'blocked';
    events.push({
      type: 'block',
      reason: 'occupancy_or_path',
      remainingLost: optimistic.stepsTaken - stepsTaken,
    });
  }

  const endUsed = resolved.jokerLapUsed;
  const remainingBudget = Math.max(
    0,
    (optimistic.remainingBudget ?? 0) + Math.max(0, optimistic.stepsTaken - stepsTaken)
  );
  const jokerChoicePending =
    resolveEffectiveJokerLapEntryChoice(
      circuit,
      numLaps,
      completedLapsStart + lapsGained,
      endUsed,
      enterJokerLapChoice
    ) == null;
  const atJokerEntry = needsJokerLapEntryChoice(circuit, resolved.position, endUsed, numLaps);
  // Pause seulement s’il reste du budget à consommer sur/après l’entrée.
  if (
    (resolved.pausedAtJokerLapEntry || (jokerChoicePending && atJokerEntry)) &&
    remainingBudget > 0
  ) {
    events.push({ type: 'stop', reason: 'joker_pause' });
    return {
      position: resolved.position,
      jokerLapUsed: endUsed,
      moveBudget,
      stepsTaken,
      remainingBudget,
      waypoints: resolved.waypoints,
      priorityDefenderIndex: resolved.priorityDefenderIndex,
      overtakes: resolved.overtakes,
      freePasses: resolved.freePasses,
      events,
      pausedAtJokerLapEntry: true,
      finishPending: false,
      lapsGained,
      stopReason: 'joker_pause',
    };
  }

  const finishPending = canFinishRace(circuit, numLaps, completedLapsStart + lapsGained, endUsed);
  if (finishPending) events.push({ type: 'finish_pending' });
  events.push({ type: 'stop', reason: stopReason });

  return {
    position: resolved.position,
    jokerLapUsed: endUsed,
    moveBudget,
    stepsTaken,
    remainingBudget: 0,
    waypoints: resolved.waypoints,
    priorityDefenderIndex: resolved.priorityDefenderIndex,
    overtakes: resolved.overtakes,
    freePasses: resolved.freePasses,
    events,
    finishPending,
    lapsGained,
    stopReason,
  };
}

/** Journalise réduction virage (1×) + logs cell + franchissements depuis des waypoints. */
function appendCornerAndCellEventsFromWaypoints(
  circuit: Circuit,
  waypoints: readonly PlayerTrackPosition[],
  initialBudget: number,
  events: TrackMoveEvent[],
  moveCtx: TrackOvertakeContext,
  playerIndex: number,
  completedLapsStart = 0
): { lapsGained: number } {
  let cornerReductionApplied = false;
  let simBudget = initialBudget;
  let lapsGained = 0;
  let lapCount = completedLapsStart;
  const cellEvents: TrackMoveEvent[] = [];

  for (let i = 1; i < waypoints.length; i++) {
    const prev = waypoints[i - 1];
    const next = waypoints[i];
    const currentCell = getPlayerTrackCell(circuit, prev);
    const nextCell = getPlayerTrackCell(circuit, next);
    const remainingBefore = simBudget;
    let traversedFull = false;

    if (
      currentCell &&
      nextCell &&
      currentCell.type !== TrackCellType.TURN &&
      nextCell.type === TrackCellType.TURN &&
      !cornerReductionApplied
    ) {
      const before = simBudget;
      simBudget = Math.ceil(simBudget / 2);
      cornerReductionApplied = true;
      events.push({ type: 'corner_reduction', before, after: simBudget });
    }

    const nextKey = trackMovementOccupancyKey(circuit, next);
    if (nextKey && moveCtx.openingRound && i < waypoints.length - 1) {
      const others = moveCtx.countOthersOnGrid(nextKey, playerIndex, next);
      if (isTrackCellOccupancyFull(others)) traversedFull = true;
    }

    simBudget = Math.max(0, simBudget - 1);
    cellEvents.push({
      type: 'cell',
      from: prev,
      to: next,
      remainingBefore,
      remainingAfter: simBudget,
      traversedFull: traversedFull || undefined,
    });

    const crossing = countLapCrossingBetween(circuit, prev, next);
    if (crossing > 0) {
      const before = lapCount;
      lapCount += crossing;
      lapsGained += crossing;
      events.push({ type: 'lap_crossing', before, after: lapCount });
    }
  }
  events.push(...cellEvents);
  return { lapsGained };
}
